import { useState } from "preact/hooks";
import { submitCapture } from "./api-client";
import type { QualifyingData, SessionState } from "./session-store";

interface Props {
  session: SessionState;
  onSubmitted: (leadId: string) => void;
}

type ContactPref = "Text" | "Email" | "Call";

export function ContactForm({ session, onSubmitted }: Props) {
  const q = session.qualifying;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pref, setPref] = useState<ContactPref>("Email");
  // Pre-fill with whatever city Benji captured in chat (if anything). The
  // server accepts city, zip, or a full address and resolves it before save.
  const [location, setLocation] = useState(q.locationCity ?? "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    // Disambiguate the location field: a bare 5-digit string is a zip; a
    // longer string is an address (which the server will parse a zip out of);
    // a short non-numeric is a city name. All three flow correctly to the
    // backend resolver.
    const locTrim = location.trim();
    const isBareZip = /^\d{5}$/.test(locTrim);
    const looksLikeAddress = /\d/.test(locTrim) && locTrim.length > 5;

    const result = await submitCapture({
      sessionId: session.sessionId,
      threadId: session.threadId,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      contactPref: pref,
      eventType: q.eventType,
      guests: q.guests,
      eventDate: q.eventDate,
      locationCity: !isBareZip && !looksLikeAddress && locTrim ? locTrim : q.locationCity,
      locationZip: isBareZip ? locTrim : undefined,
      locationAddress: looksLikeAddress ? locTrim : undefined,
      serviceWindow: q.serviceWindow,
      notes: notes.trim() || undefined,
    });
    setSubmitting(false);
    if (!result.ok || !result.leadId) {
      setError(result.message ?? "Submission failed — try again.");
      return;
    }
    onSubmitted(result.leadId);
  }

  return (
    <form className="ccb-bx-form" onSubmit={handleSubmit}>
      <div className="ccb-bx-form__eyebrow">☕ Quick handoff</div>
      <div className="ccb-bx-form__title">Where should we send your quote?</div>
      <div className="ccb-bx-form__sub">
        Three quick fields and one of our team will follow up within an hour or two.
      </div>

      <Summary qualifying={q} />

      <div className="ccb-bx-form__field">
        <label htmlFor="ccb-bx-name">Full name</label>
        <input
          id="ccb-bx-name"
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          required
        />
      </div>
      <div className="ccb-bx-form__field">
        <label htmlFor="ccb-bx-email">Email</label>
        <input
          id="ccb-bx-email"
          type="email"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          required
        />
      </div>
      <div className="ccb-bx-form__field">
        <label htmlFor="ccb-bx-phone">Phone (optional)</label>
        <input
          id="ccb-bx-phone"
          type="tel"
          value={phone}
          onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
        />
      </div>
      <div className="ccb-bx-form__field">
        <label htmlFor="ccb-bx-loc">Venue zip, city, or address (optional)</label>
        <input
          id="ccb-bx-loc"
          type="text"
          value={location}
          placeholder="90210, Beverly Hills, or 1234 Sunset Blvd, LA 90026"
          onInput={(e) => setLocation((e.target as HTMLInputElement).value)}
        />
      </div>
      <div className="ccb-bx-form__field">
        <label>Best way to reach you</label>
        <div className="ccb-bx-form__seg">
          {(["Text", "Email", "Call"] as ContactPref[]).map((p) => (
            <button
              key={p}
              type="button"
              className={p === pref ? "active" : ""}
              onClick={() => setPref(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <div className="ccb-bx-form__field">
        <label htmlFor="ccb-bx-notes">Anything we should know? (optional)</label>
        <textarea
          id="ccb-bx-notes"
          rows={2}
          placeholder="Allergies, signage notes, anything tricky about the venue…"
          value={notes}
          onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
        />
      </div>

      <p className="ccb-bx-form__consent">
        By sending, you're okay with us texting/emailing about your quote. We never share your info.
      </p>

      {error && <div className="ccb-bx-form__error">{error}</div>}

      <button
        type="submit"
        className="ccb-bx-form__submit"
        disabled={submitting || !name.trim() || !email.trim()}
      >
        {submitting ? "Sending…" : "Send my quote request →"}
      </button>
    </form>
  );
}

function Summary({ qualifying }: { qualifying: QualifyingData }) {
  const fields: Array<{ key: string; value?: string; full?: boolean }> = [
    { key: "Event", value: qualifying.eventType },
    { key: "Date", value: qualifying.eventDate },
    { key: "Guests", value: qualifying.guests ? `~${qualifying.guests}` : undefined },
    { key: "Location", value: qualifying.locationCity },
    { key: "Service", value: qualifying.serviceWindow, full: true },
  ].filter((f) => f.value);

  if (fields.length === 0) return null;

  return (
    <div className="ccb-bx-form__summary">
      {fields.map((f) => (
        <div key={f.key} className={"row" + (f.full ? " full" : "")}>
          <span className="lbl">{f.key}</span>
          <span className="val">{f.value}</span>
        </div>
      ))}
    </div>
  );
}

export interface SubmittedProps {
  name: string;
  email: string;
  eventDate?: string;
  onKeepChatting: () => void;
}

export function ContactFormSubmitted({ name, email, eventDate, onKeepChatting }: SubmittedProps) {
  const firstName = name.split(/\s+/)[0] ?? name;
  const dateLabel = eventDate ? formatDateLabel(eventDate) : "your date";
  return (
    <div className="ccb-bx-done">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className="ccb-bx-done__check">✓</div>
        <div>
          <div className="ccb-bx-done__title">You're all set, {firstName}</div>
          <div className="ccb-bx-done__sub">Confirmation sent to {email}</div>
        </div>
      </div>
      <div className="ccb-bx-done__steps">
        <Step
          n={1}
          title="Within an hour or two"
          body={`A team member emails you a written quote with a few package options for ${dateLabel}.`}
        />
        <Step
          n={2}
          title="Date held until tomorrow 5pm"
          body="Soft hold so no one else can book it while you think it over."
        />
        <Step
          n={3}
          title="30% deposit books the date"
          body="Once the deposit lands, we kick off logistics together — load-in, power, menu, signage."
        />
      </div>
      <button
        type="button"
        onClick={onKeepChatting}
        className="ccb-bx-form__submit"
        style={{ background: "transparent", color: "#0E0901", border: "1.5px solid #0E0901" }}
      >
        Keep chatting with Benji
      </button>
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="ccb-bx-done__step">
      <div className="num">{n}</div>
      <div className="body">
        <strong>{title}</strong>
        {body}
      </div>
    </div>
  );
}

function formatDateLabel(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}
